import { previousDay, streakOn } from "./scoring.js";
import { somarDias } from "./periodo.js";
import { horaLocal, type Lembretes } from "./lembrete.js";
import type { DayKey } from "./types.js";

/* ============================================================
   As notificações de disputa

   "Falta só você", "falta só a Ana", cutucar, "sua sequência acaba
   hoje", "te passaram no ranking", "a semana fecha à meia-noite". Todas
   decidem a mesma coisa — QUEM recebe e QUANDO — e por isso a decisão
   mora aqui, pura e testável; a função agendada só lê o banco, chama
   isto e envia.

   As regras que impedem de virar chatice são do produto, não do código:

   - Nada antes das 18h. Se o último prato que faltava foi o café das
     8h, o "falta só você" espera a noite; às 21h vem um último aviso.
   - Uma vez por dia de cada tipo, por grupo. Quem garante isso é o
     marcador gravado no servidor, não esta função.
   - Não empilha com o lembrete de refeição: se a pessoa tem um lembrete
     marcado perto da hora, este aviso fica para a próxima rodada.
   - Só conta quem está ATIVO (postou nos últimos 7 dias). Quem sumiu do
     grupo não pode ser "o único que falta" para sempre.
   ============================================================ */

/** A janela em que avisos de disputa podem sair, no fuso do grupo. */
export const JANELA_DA_DISPUTA = { de: "18:00", ate: "21:59" } as const;
/** Hora do último aviso para quem ainda falta. */
export const HORA_DO_ULTIMO_AVISO = "21:00";
/** Hora do "sua sequência acaba hoje". */
export const HORA_DA_SEQUENCIA = "20:00";
/** De quantos dias a sequência passa a merecer aviso. */
export const SEQUENCIA_QUE_MERECE_AVISO = 3;
/** Quantos dias sem postar tiram a pessoa da conta do "todo mundo". */
export const DIAS_PARA_CONTAR_COMO_ATIVO = 7;
/** Quanto antes e depois de um lembrete o aviso de disputa espera. */
export const FOLGA_DO_LEMBRETE_MIN = 45;

export interface PessoaDoDia {
  uid: string;
  name: string;
  days?: readonly DayKey[];
  jokerDays?: readonly DayKey[];
}

/** Postou em algum dos últimos N dias (contando hoje)? */
export function estaAtiva(p: PessoaDoDia, hoje: DayKey, dias = DIAS_PARA_CONTAR_COMO_ATIVO): boolean {
  const desde = somarDias(hoje, -(dias - 1));
  return (p.days ?? []).some((d) => d >= desde && d <= hoje);
}

export interface RetratoDoDia {
  ativos: PessoaDoDia[];
  postaram: PessoaDoDia[];
  faltam: PessoaDoDia[];
}

/** Quem está ativo, quem já postou hoje e quem falta — só entre os ativos. */
export function retratoDoDia(pessoas: readonly PessoaDoDia[], hoje: DayKey): RetratoDoDia {
  const ativos = pessoas.filter((p) => estaAtiva(p, hoje));
  const postou = (p: PessoaDoDia) => (p.days ?? []).includes(hoje);
  return {
    ativos,
    postaram: ativos.filter(postou),
    faltam: ativos.filter((p) => !postou(p)),
  };
}

/** "HH:MM" está dentro da janela da disputa? */
export const naJanelaDaDisputa = (hora: string): boolean =>
  hora >= JANELA_DA_DISPUTA.de && hora <= JANELA_DA_DISPUTA.ate;

export type AvisoDeDisputa =
  | { tipo: "falta-so-voce"; para: string; ultimo: boolean }
  | { tipo: "falta-so-fulano"; para: string; quem: PessoaDoDia }
  | { tipo: "faltam-poucos"; para: string; outros: PessoaDoDia[] };

/**
 * O que sai NESTA rodada da varredura (antes dos marcadores de "já
 * enviado hoje", que são do servidor).
 *
 * - Falta UMA pessoa (grupo com 2+ ativos): ela recebe "falta só você";
 *   quem já postou recebe "falta só a Ana", com o atalho de cutucar.
 *   A partir de `HORA_DO_ULTIMO_AVISO` o dela vira o último aviso — que
 *   tem marcador próprio, então chega mesmo depois do primeiro.
 * - Faltam 2 ou 3 num grupo de 4+ ativos: só quem falta recebe "faltam
 *   só você e o João". Quem já postou não é incomodado: com três
 *   faltando ainda não é a hora de cutucar ninguém.
 */
export function avisosDaRodada(retrato: RetratoDoDia, hora: string): AvisoDeDisputa[] {
  if (!naJanelaDaDisputa(hora)) return [];
  const { ativos, postaram, faltam } = retrato;
  if (faltam.length === 1 && ativos.length >= 2) {
    const quem = faltam[0]!;
    return [
      { tipo: "falta-so-voce", para: quem.uid, ultimo: hora >= HORA_DO_ULTIMO_AVISO },
      ...postaram.map((p) => ({ tipo: "falta-so-fulano" as const, para: p.uid, quem })),
    ];
  }
  if (faltam.length >= 2 && faltam.length <= 3 && ativos.length >= 4) {
    return faltam.map((p) => ({
      tipo: "faltam-poucos" as const, para: p.uid, outros: faltam.filter((o) => o.uid !== p.uid),
    }));
  }
  return [];
}

const primeiro = (nome: string) => nome.trim().split(/\s+/)[0] || nome;
const juntar = (nomes: string[]) =>
  nomes.length <= 1 ? (nomes[0] ?? "") : `${nomes.slice(0, -1).join(", ")} e ${nomes.at(-1)}`;

/**
 * O texto de cada aviso. `missao` é o nome da coletiva de "todo dia"
 * valendo hoje, se houver — é ela que dá peso ao "falta só você".
 */
export function textoDaDisputa(
  a: AvisoDeDisputa, grupo: string, missao?: string,
): { title: string; body: string } {
  switch (a.tipo) {
    case "falta-so-voce":
      return {
        title: a.ultimo ? `${grupo}: último aviso` : `${grupo}: falta só você!`,
        body: missao
          ? `O grupo inteiro já postou hoje. Posta o seu pra todo mundo ganhar o ${missao}.`
          : "O grupo inteiro já postou hoje. Só falta o seu prato.",
      };
    case "falta-so-fulano":
      return {
        title: `${grupo}: falta só ${primeiro(a.quem.name)}`,
        body: missao
          ? `Falta só ${primeiro(a.quem.name)} pra todo mundo ganhar o ${missao} hoje. Dá pra cutucar no feed.`
          : `Todo mundo já postou hoje, menos ${primeiro(a.quem.name)}. Dá pra cutucar no feed.`,
      };
    case "faltam-poucos":
      return {
        title: `${grupo}: faltam só ${a.outros.length + 1}`,
        body: `Faltam só você e ${juntar(a.outros.map((o) => primeiro(o.name)))} postarem hoje.`,
      };
  }
}

/* ---------- cutucar ---------- */

export type RecusaDaCutucada = "fora-da-hora" | "ja-postou" | "voce-mesmo" | "fora-do-grupo";

export const MOTIVO_DA_CUTUCADA: Record<RecusaDaCutucada, string> = {
  "fora-da-hora": "Dá pra cutucar entre 18h e 22h.",
  "ja-postou": "Essa pessoa já postou hoje.",
  "voce-mesmo": "Não dá pra cutucar você mesmo.",
  "fora-do-grupo": "Essa pessoa não está neste desafio.",
};

/**
 * Pode cutucar? O "uma vez por pessoa por dia" e o bloqueio são
 * conferidos no servidor — o bloqueio porque só ele pode ler a lista de
 * quem a pessoa bloqueou, e a cutucada recusada por bloqueio responde
 * igual a uma que deu certo, para não revelar o bloqueio.
 */
export function conferirCutucada(
  p: { autor: string; alvo: PessoaDoDia | null; hoje: DayKey; hora: string },
): RecusaDaCutucada | null {
  if (!p.alvo) return "fora-do-grupo";
  if (p.alvo.uid === p.autor) return "voce-mesmo";
  if (!naJanelaDaDisputa(p.hora)) return "fora-da-hora";
  if ((p.alvo.days ?? []).includes(p.hoje)) return "ja-postou";
  return null;
}

export const textoDaCutucada = (autor: string, grupo: string) => ({
  title: `${grupo}: ${primeiro(autor)} tá te cutucando`,
  body: "Falta só você! Posta o prato de hoje.",
});

/* ---------- sequência ---------- */

/**
 * O tamanho da sequência que acaba hoje se a pessoa não postar: os dias
 * seguidos até ONTEM (vale-falta conta). 0 se ela já postou hoje ou se
 * não há sequência para perder.
 */
export function sequenciaEmRisco(p: PessoaDoDia, hoje: DayKey): number {
  const dias = new Set<DayKey>([...(p.days ?? []), ...(p.jokerDays ?? [])]);
  if (dias.has(hoje)) return 0;
  return streakOn(dias, previousDay(hoje));
}

export const textoDaSequencia = (n: number, grupo: string) => ({
  title: `${grupo}: sua sequência acaba hoje`,
  body: `Sua sequência de ${n} dias acaba hoje se você não postar.`,
});

/* ---------- ultrapassagem ---------- */

export interface Posicao { uid: string; name: string; pontos: number }

const ordenar = (l: readonly Posicao[]) =>
  [...l].sort((a, b) => b.pontos - a.pontos || a.name.localeCompare(b.name, "pt-BR"));

/**
 * Quem `uid` passou quando os pontos dele foram de `antes` para
 * `depois`: estava à frente (ou empatado e à frente na ordem) e agora
 * está atrás. Devolve a NOVA posição de cada um que caiu.
 */
export function quemFoiUltrapassado(
  placar: readonly Posicao[], uid: string, antes: number, depois: number,
): { uid: string; name: string; posicao: number }[] {
  if (!(depois > antes)) return [];
  const com = (pts: number) => ordenar(placar.map((p) => (p.uid === uid ? { ...p, pontos: pts } : p)));
  const velho = com(antes), novo = com(depois);
  const iVelho = velho.findIndex((p) => p.uid === uid);
  const iNovo = novo.findIndex((p) => p.uid === uid);
  if (iNovo >= iVelho) return [];
  const passados = new Set(velho.slice(iNovo, iVelho).map((p) => p.uid));
  return novo
    .map((p, i) => ({ uid: p.uid, name: p.name, posicao: i + 1, pontos: p.pontos }))
    .filter((p) => passados.has(p.uid) && p.pontos > 0)
    .map(({ uid: u, name, posicao }) => ({ uid: u, name, posicao }));
}

export const textoDaUltrapassagem = (quem: string, posicao: number, grupo: string) => ({
  title: `${grupo}: ${primeiro(quem)} te passou!`,
  body: `Você caiu pra ${posicao}º na temporada. Ainda dá tempo de virar.`,
});

/* ---------- fim da temporada ---------- */

/** Domingo, às 20h: a semana fecha à meia-noite. */
export const HORA_DO_FIM_DA_TEMPORADA = "20:00";

export const textoDoFimDaTemporada = (grupo: string, lider: string | null) => ({
  title: `${grupo}: última chance`,
  body: lider
    ? `A semana fecha à meia-noite. ${primeiro(lider)} está na frente.`
    : "A semana fecha à meia-noite.",
});

/* ---------- missões ---------- */

export const textoDaMissaoFechada = (quem: string, missao: string, emoji: string, bonus: number, grupo: string) => ({
  title: `${grupo}: ${emoji || "🎯"} ${missao}`,
  body: `${primeiro(quem)} fechou a ${missao}! +${bonus}`,
});

/** Quantos faltam para uma coletiva de pratos merecer o "quase lá". */
export const FALTA_POUCO_NA_COLETIVA = 3;

export const textoDaColetivaPerto = (faltam: number, missao: string, grupo: string) => ({
  title: `${grupo}: quase lá`,
  body: `Faltam ${faltam} ${faltam === 1 ? "prato" : "pratos"} pro grupo fechar o ${missao}!`,
});

/* ---------- não empilhar ---------- */

const minutos = (hora: string) => {
  const [h, m] = hora.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/**
 * A pessoa tem um lembrete (de refeição ou a última chamada) marcado
 * perto deste instante? Se sim, o aviso de disputa espera: dois avisos
 * do mesmo app com minutos de diferença é o que faz alguém desligar
 * todos.
 */
export function lembretePerto(
  lembretes: Lembretes | null | undefined, agora: Date, folga = FOLGA_DO_LEMBRETE_MIN,
): boolean {
  if (!lembretes) return false;
  const agoraMin = minutos(horaLocal(agora, lembretes.fuso));
  const horas = [
    ...lembretes.slots.filter((s) => s.ligado).map((s) => s.hora),
    ...(lembretes.ultimaChamada.ligado ? [lembretes.ultimaChamada.hora] : []),
  ];
  return horas.some((h) => Math.abs(minutos(h) - agoraMin) <= folga);
}
